import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useKey } from 'react-use'
import gsap from 'gsap'

import CtaButton from '@/components/base/cta-button'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type NoResultsModalProps = {
  open: boolean
  onClose: () => void
  className?: string
}

const NoResultsModal = ({ open, onClose, className }: NoResultsModalProps) => {
  const [mounted, setMounted] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const { strings } = useStore()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!rootRef.current) return
    gsap.to(rootRef.current, {
      opacity: open ? 1 : 0,
      pointerEvents: open ? 'auto' : 'none',
      duration: 0.3,
      ease: 'power2.out'
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    const prevBodyOverflow = document.body.style.overflow
    const prevHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.documentElement.style.overflow = prevHtmlOverflow
    }
  }, [open])

  useEffect(() => {
    if (open && closeButtonRef.current) {
      closeButtonRef.current.focus()
    }
  }, [open])

  useKey(
    'Escape',
    () => {
      if (open) onClose()
    },
    {},
    [open, onClose]
  )

  if (!mounted) return null

  return createPortal(
    <div
      ref={rootRef}
      className={cn(
        'pointer-events-none fixed inset-0 z-[110] flex h-[calc(100*var(--lvh))] items-center justify-center bg-[black]/60 opacity-0',
        className
      )}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
    >
      {open && (
        <div className="relative mx-margin flex w-full max-w-[570px] flex-col justify-between gap-spacing-xl bg-[#d9d9d9] p-spacing-lg">
          {/* Header row */}
          <div className="flex items-start justify-between gap-spacing-xs">
            <Typography
              variant="large"
              className="condensed text-text-heading-light"
            >
              {strings?.labels?.sales_modal_title ||
                'La Ricerca non ha prodotto risultati, riprova o contattaci per ulteriori informazioni'}
            </Typography>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="flex size-[60px] shrink-0 items-center justify-center rounded-full border border-[rgba(0,0,0,0.3)] text-text-heading-light transition-opacity hover:opacity-70"
            >
              <SVGIcon symbol="close" className="size-[16px]" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="light flex flex-wrap gap-spacing-xs">
            {strings?.labels?.sales_modal_contact_link?.url ? (
              <CtaButton
                href={strings?.labels?.sales_modal_contact_link?.url}
                variant="tertiary"
                className="flex-1"
              >
                {strings?.labels?.sales_modal_contact_link?.title}
              </CtaButton>
            ) : null}
            {strings?.labels?.sales_modal_discover_link?.url ? (
              <CtaButton
                href={strings?.labels?.sales_modal_discover_link?.url || ''}
                variant="secondary"
                className="flex-1"
              >
                {strings?.labels?.sales_modal_discover_link?.title ||
                  'Scopri le Agenzie'}
              </CtaButton>
            ) : null}
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}

export default NoResultsModal
